import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/lib/utils';

const inputVariants = cva(
  'input-base file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 file:inline-flex file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      size: {
        large: 'input-large',
        small: 'input-small',
      },
      state: {
        default: 'input-default-state input-focus',
        filled: 'input-filled-state input-focus',
        disabled: 'input-disabled-state',
        edit: 'input-edit-state input-focus',
        error: 'input-error-state input-error-focus',
      },
    },
    defaultVariants: {
      size: 'small',
      state: 'default',
    },
  }
);

export interface InputProps extends Omit<React.ComponentProps<'input'>, 'size'>, VariantProps<typeof inputVariants> {
  showDropdownIcon?: boolean;
}

function Input({ className, type, size, state, showDropdownIcon = false, disabled, ...props }: InputProps) {
  // Auto-detect state based on props
  const effectiveState = disabled ? 'disabled' : state;

  return (
    <div className='relative'>
      <input
        type={type}
        data-slot='input'
        className={cn(inputVariants({ size, state: effectiveState }), className)}
        disabled={disabled}
        {...props}
      />
      {showDropdownIcon && (
        <svg
          className='input-dropdown-icon'
          width='16'
          height='16'
          viewBox='0 0 16 16'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <path
            d='M4 6L8 10L12 6'
            stroke='currentColor'
            strokeWidth='1.5'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
        </svg>
      )}
    </div>
  );
}

export { Input, inputVariants };
